import { useEffect, useState } from 'react';
import api from '../lib/api';

/**
 * Component that loads and injects custom scripts from settings
 * - Head scripts are injected into document.head
 * - Footer scripts are injected at the end of document.body
 * 
 * Note: Scripts are injected directly without sanitization since they come
 * from admin-controlled settings. This allows tracking codes like Google Analytics,
 * Meta Pixel, Clickio, etc. to work properly.
 */
export const CustomScripts = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadAndInjectScripts = async () => {
      try {
        const res = await api.get('/settings/public');
        const { custom_head_code, custom_footer_code } = res.data || {};

        // Inject head code
        if (custom_head_code && custom_head_code.trim()) {
          injectCode(custom_head_code, 'head', 'custom-head-scripts');
        }

        // Inject footer code
        if (custom_footer_code && custom_footer_code.trim()) {
          injectCode(custom_footer_code, 'body', 'custom-footer-scripts');
        }

        setLoaded(true);
      } catch (error) {
        console.error('Error loading custom scripts:', error);
      }
    };

    loadAndInjectScripts();

    // Cleanup function to remove injected scripts on unmount
    return () => {
      const headScripts = document.getElementById('custom-head-scripts');
      const footerScripts = document.getElementById('custom-footer-scripts');
      if (headScripts) headScripts.remove();
      if (footerScripts) footerScripts.remove();
    };
  }, []);

  return null; // This component doesn't render anything visible
};

/**
 * Helper function to inject HTML/script code into the DOM
 * @param {string} code - The HTML/script code to inject
 * @param {string} target - 'head' or 'body'
 * @param {string} containerId - ID for the container element
 */
function injectCode(code, target, containerId) {
  // Check if already injected
  if (document.getElementById(containerId)) {
    return;
  }

  // Create a marker to track injection
  const marker = document.createElement('div');
  marker.id = containerId;
  marker.style.display = 'none';
  document.body.appendChild(marker);

  // Parse the code using DOMParser (safer than innerHTML)
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${code}</body>`, 'text/html');
  const children = Array.from(doc.body.childNodes);
  
  children.forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      
      if (tagName === 'script') {
        // Handle script tags specially - need to recreate them for execution
        const newScript = document.createElement('script');
        
        // Copy attributes
        Array.from(node.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        
        // Copy inline content
        if (node.textContent) {
          newScript.textContent = node.textContent;
        }
        
        // Append to appropriate location
        if (target === 'head') {
          document.head.appendChild(newScript);
        } else {
          document.body.appendChild(newScript);
        }
      } else if (tagName === 'style' || tagName === 'link' || tagName === 'meta') {
        // Style, link, and meta tags go to head
        document.head.appendChild(node.cloneNode(true));
      } else if (tagName === 'noscript') {
        // noscript tags - append where specified
        if (target === 'head') {
          document.head.appendChild(node.cloneNode(true));
        } else {
          document.body.appendChild(node.cloneNode(true));
        }
      }
    } else if (node.nodeType === Node.COMMENT_NODE) {
      // Preserve comments (useful for identifying injected code)
      const comment = document.createComment(node.textContent);
      if (target === 'head') {
        document.head.appendChild(comment);
      } else {
        document.body.appendChild(comment);
      }
    }
  });
}

export default CustomScripts;
